import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo, stats } from '../data/mock';

const HeroNew = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#f5f0e6] pt-24 md:pt-20">
      {/* Soft decorative blobs in new gradient palette */}
      <div className="absolute top-20 -left-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#b88865]/20 to-[#8b5a3c]/15 blur-3xl"></div>
      <div className="absolute bottom-20 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#6b4226]/20 to-[#a06d4d]/15 blur-3xl"></div>
      <div className="absolute top-1/3 right-1/4 w-64 h-64 rounded-full bg-[#e0d0b8]/40 blur-2xl"></div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 lg:px-8">
        <div className={`transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Two-column: text + photo */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-12 items-center mb-16">
            {/* LEFT - text */}
            <div data-testid="hero-text-block">
              {/* Quirky greeting */}
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-[#8b5a3c]" />
                <span className="font-handwriting text-2xl gradient-text-animated">Hey there! I'm</span>
              </div>

              {/* Main heading */}
              <h1 className="text-6xl md:text-7xl lg:text-[5.5rem] font-bold mb-6 leading-[0.95]">
                <span className="font-serif text-[#2d2a26]">{personalInfo.name.split(' ')[0]}</span>
                <br />
                <span className="font-display gradient-text-animated text-7xl md:text-8xl lg:text-[8rem]">
                  {personalInfo.name.split(' ')[1]}
                </span>
              </h1>

              {/* Title with playful description */}
              <div className="mb-8 max-w-2xl">
                <h2 className="text-2xl md:text-3xl font-semibold mb-3 font-display">
                  <span className="gradient-text">{personalInfo.title}</span>
                </h2>
                <p className="text-2xl text-[#6b4226] font-handwriting mb-5">
                  {personalInfo.tagline}
                </p>
                <p className="text-xl md:text-[1.35rem] text-[#4a3a2c] leading-[1.7]">
                  I sit where{' '}
                  <span className="font-display gradient-text font-bold text-2xl md:text-3xl">technology meets strategy</span>
                  {' '}- turning complex business requirements into structured project plans, seamless solutions, and outcomes that actually ship. Based in London. With a foundation in Computer Science and a Master's in Business Management, I bring both technical understanding and commercial awareness to product delivery conversations.
                </p>
              </div>

              {/* CTA Button */}
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  data-testid="hero-cta-chat"
                  className="btn-gradient text-white font-semibold px-8 py-6 text-base rounded-full shadow-lg border-0"
                  onClick={scrollToContact}
                >
                  Let's chat!
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* RIGHT - photo */}
            <div className="flex justify-center lg:justify-end" data-testid="hero-photo-block">
              <div className="relative">
                {/* Soft floating gradient blobs behind photo */}
                <div className="absolute -inset-6 bg-gradient-to-br from-[#b88865]/25 via-[#8b5a3c]/20 to-[#6b4226]/25 blur-2xl rounded-full"></div>
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full gradient-bg opacity-30 blur-xl animate-float"></div>

                {/* Photo with gradient ring */}
                <div className="photo-ring shadow-2xl relative">
                  <img
                    src={personalInfo.photo}
                    alt={personalInfo.name}
                    data-testid="hero-photo"
                    className="w-64 h-64 md:w-80 md:h-80 object-cover object-top rounded-full bg-[#fffcf6]"
                    style={{ objectPosition: '50% 18%' }}
                  />
                </div>

                {/* Handwritten caption */}
                <div className="absolute -bottom-2 -left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-2xl shadow-lg border border-[#e0d0b8] rotate-[-4deg]">
                  <p className="font-handwriting text-xl text-[#6b4226]">that's me!</p>
                </div>

                {/* Sparkle decoration */}
                <Sparkles className="absolute -top-3 -left-3 w-8 h-8 text-[#8b5a3c] animate-pulse" />
              </div>
            </div>
          </div>

          {/* Stats Grid - 4 boxes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="hero-stats">
            {stats.map((stat, index) => {
              const IconComponent = require('lucide-react')[stat.icon];
              return (
                <div
                  key={index}
                  data-testid={`hero-stat-${index}`}
                  className="bg-white/70 backdrop-blur-sm border border-[#e0d0b8] rounded-2xl p-6 hover:bg-white/90 transition-all duration-300 hover:scale-105 hover:border-[#8b5a3c]/40 hover:shadow-xl group reveal"
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  {IconComponent && (
                    <IconComponent className="w-8 h-8 text-[#8b5a3c] mb-3 group-hover:scale-110 group-hover:text-[#6b4226] transition-all duration-300" />
                  )}
                  <div className="text-3xl font-bold mb-1 font-display">
                    <span className="gradient-text">{stat.value}</span>
                  </div>
                  <div className="text-sm text-[#737373]">{stat.label}</div>
                </div>
              );
            })}
          </div>

          {/* Fun tagline */}
          <div className="mt-12 text-center">
            <p className="text-[#6b4226]/70 font-handwriting text-xl">
              Scroll down to see what I've been up to ↓
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroNew;
